import React, { useEffect, useMemo, useState } from 'react';
import RequestFormLayout from 'components/layout/RequestFormLayout';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CombinedStates } from 'core/types';
import DateSelector from 'components/Form/DateSelector';
import ShiftSelector from 'components/Form/ShiftSelector';
import type { SaveShiftValues } from 'components/Form/ShiftSelector/types';
import { notification } from 'antd';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import useRequestFlow from 'core/guestBooking/useRequestFlow';
import { Shift, VisitRequestFormResponsePayload } from 'core/visitRequestForm/types';
import CostCode from 'components/Form/CostCode';
import { isEmpty } from 'underscore';
import moment from 'moment';
import { ADMIN_ACCESS_LEVELS, isCostApprovalExemptEmail } from 'core/utils/value';
import useIsGuest from 'core/guestBooking/useIsGuest';
import { addHoursToTime, formatTime, parseUsOrIso, shiftIsOnCalendarDate, usToIso } from 'core/utils/date';
import { emergencyWindowEnd, splitEmergencyWindow } from 'core/utils/emergencyShift';
import { buildRotating247Blocks } from 'core/utils/rotating247';
import { unwrapResult } from '@reduxjs/toolkit';
import latestShiftOptionId from 'core/utils/shiftOptionIds';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';
import { BankHolidayMap, bankHolidayDates, fetchBankHolidays, toCalendarHolidays } from 'core/utils/bankHolidays';
import { BankHolidayModalState, bankHolidayChargeDates } from 'core/utils/bankHolidayWarning';
import BankHolidayWarningModal from 'components/Modals/BankHolidayWarningModal';
import { SiteAvailability, HandleDateValues } from './types';

export interface VisitDatesProps {
    id: number | null;
    date: string,
    saved: boolean,
    shiftName?: string | undefined,
    startTime?: string | undefined,
    endTime?: string | undefined,
    range?: boolean | undefined,
    startDate?: string | undefined,
    endDate?: string | undefined,
    shiftType?: number | string | undefined,
    blockDate?: string | undefined | null,
    bookingTimeCritical?: boolean,
}

interface TimeSlot {
    label: string;
    startTime: string;
    endTime: string;
}

interface ShiftOption {
    id?: number;
    name: string;
    value: string;
    timeSlots: TimeSlot[];
}

interface ShiftSelected {
    _tempIndex?: number | undefined;
    blockDate?: string | undefined | null;
    id?: number | null;
    day?: string;
    date: string;
    shift: string | undefined;
    shiftType: number | string | undefined;
    startTime: string | undefined;
    endTime: string | undefined;
    bookingTimeCritical?: boolean;
    saved?: boolean;
}

interface customShiftProps {
    startTime: string;
    endTime: string;
    date: string;
    blockDate: string | undefined | null;
}

/**
 * The day a block BELONGS to (its group identity for removal/edit) is its start
 * day — `blockDate` when set, otherwise the block's own `date`. A 24/7 shift
 * that starts after 19:00 spills continuation blocks onto later calendar days
 * but they still belong to the start day, so we always reconcile by this value.
 *
 * Two helpers because the two record shapes store dates in different formats,
 * and the return is always a plain string so call sites need no undefined guard:
 *  - selectedShift (ShiftSelected): `date` is ISO, `blockDate` is "MM-DD-YYYY"
 *    → normalise to ISO for comparison against the ISO removed-dates list.
 *  - visitDates (VisitDatesProps): both are "MM-DD-YYYY" → return as-is.
 */
const shiftOwningDayIso = (shift: ShiftSelected): string => (
    shift.blockDate ? usToIso(shift.blockDate) : shift.date
);

const visitDateOwningDayUs = (item: VisitDatesProps): string => (
    item.blockDate || item.date
);

// SiteShift::TYPE_OPEN_DAY and TYPE_ALL_DAY — the two the backend leaves on offer
// for a user restricted to bookings that carry no billing charges. Open Day bills
// nothing by definition, and the 24/7 option only appears on a 24/7 site, where
// nothing is chargeable at all.
const ALWAYS_FREE_SHIFT_TYPES = [40, 20];

const DateSelectionForm: React.FC = () => {
    const navigation = useNavigate();
    const { state: navState } = useLocation();
    const { id } = useParams<{ id: string }>();
    const flow = useRequestFlow();
    const { stepData, visitRequest: visitRequestState, isFetching, isUpdating } = useSelector((state: CombinedStates) => state.visitRequestForm);
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const isGuest = useIsGuest();
    // The "Critical time" custom shift option is admin-only. Require BOTH a
    // not-guest URL and an admin role — the request form is shared with the
    // public guest booking flow where persisted userData can't be trusted.
    const isAdmin = !isGuest && ADMIN_ACCESS_LEVELS.includes(userData?.role?.accessLevel);
    // This user may only book shifts that carry no billing charges, so the backend
    // has narrowed the pickable shift types to Open Day (plus 24/7 on a 24/7 site).
    // Derived from allowedShiftTypes rather than a flag of its own, because that is
    // what the shift-option filtering below already keys off.
    const isOpenDayOnly = (stepData?.allowedShiftTypes ?? []).length > 0
        && (stepData?.allowedShiftTypes ?? []).every((type) => ALWAYS_FREE_SHIFT_TYPES.includes(Number(type)));
    // Nothing is chargeable on a 24/7 site, so every date stays selectable there;
    // elsewhere a restricted user only gets the site's open days.
    const restrictToOpenDays = isOpenDayOnly && !stepData?.enabled24_7;
    // Admin-only number of officers per shift (1-10). The backend fans each
    // shift into this many billed officer slots.
    const [numberOfOfficers, setNumberOfOfficers] = useState<number>(1);
    const [selectedDates, setSelectedDates] = useState<Array<string>>([]);
    const [visitDates, setVisitDates] = useState<VisitDatesProps[]>([]);
    const [shiftOptions, setShiftOptions] = useState<ShiftOption[]>([]);
    const [selectedShift, setSelectedShift] = useState<ShiftSelected[]>([]);
    // A bank holiday suspends the site's open window, so a shift on one is
    // charged where it would normally be free — the calendar toggle and the
    // billed-hours figure both read this.
    const [bankHolidays, setBankHolidays] = useState<BankHolidayMap>({});
    const holidays = useMemo(() => toCalendarHolidays(bankHolidays), [bankHolidays]);
    const bankHolidayList = useMemo(() => bankHolidayDates(bankHolidays), [bankHolidays]);
    const [bankHolidayAcknowledged, setBankHolidayAcknowledged] = useState<boolean>(false);
    const [bankHolidayModal, setBankHolidayModal] = useState<BankHolidayModalState>({
        open: false,
        navigationState: null,
    });
    const [code, setCode] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [requireEmail, setRequireEmail] = useState<boolean>(false);
    const [saveTotalHours, setSaveTotalHours] = useState<number>(0);
    const [minimumCalendarDate, setMinimumCalendarDate] = useState<string>('');
    const amending = navState?.amending;
    // Amending a request that was booked as an Emergency Request (any date is
    // flagged time critical): lock the shift step to Custom + Emergency Request.
    const isEmergencyAmendment = Boolean(amending && visitRequestState.dates?.some((date) => date.bookingTimeCritical));

    // Auto-cleanup request if user leaves without completing (never for a guest
    // booking — the draft is owned by the admin, not the guest).
    useFormCleanup({
        requestId: (!amending && !flow.guestMode) ? visitRequestState.encryptedId : null,
        formBasePath: '/visitor-request/',
        deleteEndpoint: 'visit-request',
    });

    useEffect(() => {
        fetchBankHolidays().then(setBankHolidays);
    }, []);

    // The saved shifts that a bank holiday turns from free into chargeable.
    const bankHolidayCharges = useMemo(() => bankHolidayChargeDates(
        visitDates.filter((date) => date.saved && date.startTime && date.endTime),
        { enabled24_7: stepData?.enabled24_7 ?? false, openDays: stepData?.openDays },
        bankHolidays,
    ), [visitDates, bankHolidays, stepData?.enabled24_7, stepData?.openDays]);

    // Warn again if the affected dates change — adding another bank holiday, or
    // editing one of these shifts, is a fresh charge to be told about. Changing
    // the cost code is not.
    const bankHolidayKey = bankHolidayCharges.map((charge) => charge.date).join('|');
    useEffect(() => {
        setBankHolidayAcknowledged(false);
    }, [bankHolidayKey]);

    const siteAvailability : SiteAvailability = {
        site: visitRequestState?.site,
        approvedVisits: stepData?.approvedVisits || [],
    };

    useEffect(() => {
        // Cost approval only applies to chargeable visits. Open-day-only
        // bookings and 24/7 sites bill zero hours, so there is nothing for a
        // cost approver to sign off and no email is needed.
        if (visitRequestState.costApproverByPass || saveTotalHours <= 0) {
            setRequireEmail(false);
        } else if (visitRequestState?.requester?.email) {
            setRequireEmail(!isCostApprovalExemptEmail(visitRequestState.requester.email));
        }
    }, [saveTotalHours, visitRequestState.costApproverByPass, visitRequestState?.requester?.email]);

    const handleNextClick = (navigationState: Record<string, unknown>, skipBankHolidayCheck = false) => {
        let error = { message: '', description: '' };
        if (isEmpty(selectedShift)) {
            error = {
                message: 'Shift Not Selected',
                description: 'Please select a shift to proceed',
            };
        }

        if (visitDates.some((date) => !date.saved)) {
            error = {
                message: 'Shift Not Saved',
                description: 'Please save the shift to proceed',
            };
        }

        // A restricted user can't resolve a chargeable shift by supplying a cost
        // code, so this is settled before anything else. The backend rejects it
        // too — this only saves the round trip.
        if (isOpenDayOnly && saveTotalHours > 0) {
            error = {
                message: 'Open Day Bookings Only',
                description: bankHolidayCharges[0]
                    ? `${parseUsOrIso(bankHolidayCharges[0].date).format('DD/MM/YYYY')} is a bank holiday, `
                        + 'so the shift is chargeable. '
                        + 'Your account is limited to open day bookings — please choose another date.'
                    : 'Your account is limited to open day bookings. Please choose an Open Day shift.',
            };
        }

        if (error.message !== '' && error.description !== '') {
            notification.error(error);
            return;
        }

        // Ahead of the cost-code check below: a bank holiday is why a booking
        // that cost nothing yesterday now needs a cost code, and "Cost Code Not
        // Provided" on its own explains none of that.
        if (!skipBankHolidayCheck && bankHolidayCharges.length > 0 && !bankHolidayAcknowledged) {
            setBankHolidayModal({ open: true, navigationState });
            return;
        }

        if (!isCostApprovalExemptEmail(email) && requireEmail) {
            error = {
                message: 'Invalid Email',
                description: 'Please enter a valid Vodafone email to proceed',
            };
        }

        if (email === '' && requireEmail) {
            error = {
                message: 'Email Not Provided',
                description: 'Please fill an email to proceed',
            };
        }

        // Cost code is only mandatory when there are billable hours. Open days
        // and 24/7 sites have 0 billed hours, so the cost code is N/A.
        if (saveTotalHours > 0 && code === '') {
            error = {
                message: 'Cost Code Not Provided',
                description: 'Please fill a cost code to proceed',
            };
        }

        if (error.message !== '' && error.description !== '') {
            notification.error(error);
            return;
        }
        if (selectedDates !== null && (id !== undefined || flow.guestMode) && !isFetching && !isUpdating) {
            flow.updateStep(2, {
                selectedDates: selectedShift,
                costCode: {
                    // Nothing billable means no cost code and no cost approver,
                    // otherwise the request would sit at Pending Cost Approval
                    // for a visit that costs nothing.
                    costCentre: saveTotalHours > 0 ? code : '',
                    costApprovalEmail: saveTotalHours > 0 ? email : '',
                },
                amending,
                totalHours: saveTotalHours,
                // Admin-only: fan each shift into this many officer rows. Non-admins
                // always send 1 (the backend enforces this too).
                numberOfOfficers: isAdmin ? numberOfOfficers : 1,
                forcedStep: 2,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    flow.goToStep(3, navigationState);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const handleBackClick = (navigationState: Record<string, unknown>) => {
        // Guests start at dates (site is admin-set), so there's no earlier step.
        if (flow.guestMode) {
            return;
        }
        navigation(`/visitor-request/${id}/step/1`, { state: navigationState });
    };

    const handleInitialLoad = () => {
        if (id !== undefined || flow.guestMode) {
            flow.loadStep(2, amending);
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Visit Request',
                description: 'Please select a visit request to proceed',
            });
        }
    };

    const formatDate = (dateString: string) => {
        // Parse explicitly: inputs are MM-DD-YYYY (form state) or YYYY-MM-DD /
        // ISO (API). new Date("MM-DD-YYYY") returns Invalid Date in Safari,
        // which surfaced as "Invalid date" on the calendar.
        const parsed = moment(dateString, ['MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
        return parsed.isValid() ? parsed.format('MM-DD-YYYY') : dateString;
    };

    useEffect(() => {
        handleInitialLoad();
    }, []);

    const handleDatesSelect = (values: HandleDateValues) => {
        if (values.dates && !values.dates.includes('Invalid date')) {
            let validDate = values.dates;

            if (!values.includeWeekends) {
                validDate = validDate.filter((date) => {
                    const day = new Date(date).getDay();
                    return day !== 0 && day !== 6;
                });
            }

            if (!values.includeBankHolidays) {
                validDate = validDate.filter((date) => !holidays.includes(date));
            }

            // Preserve past/ongoing dates already saved on this request that
            // can no longer be picked in the calendar because they fall before
            // the minimum selectable date. When extending an amended request
            // the user must NOT silently lose dates that have already started
            // or elapsed — the backend deletes any saved date missing from the
            // payload, so they have to stay in selectedDates / selectedShift /
            // visitDates. Parse with an explicit format; Safari rejects
            // `new Date('MM-DD-YYYY')`.
            const minMoment = moment(minimumCalendarDate, 'YYYY-MM-DD');
            const isLockedPastDate = (usDate: string) => (
                minimumCalendarDate !== '' && moment(usDate, 'MM-DD-YYYY').isBefore(minMoment, 'day')
            );
            const lockedDates = visitDates
                .filter((vd) => vd.saved && isLockedPastDate(vd.date))
                .map((vd) => vd.date);

            // Merge the locked past dates into the new calendar selection so
            // they are always retained regardless of what the calendar returns.
            const mergedValidDate = Array.from(new Set([...validDate, ...lockedDates]));

            setSelectedDates(mergedValidDate.map(date => formatDate(date)));

            // A previously-selected date is only genuinely "removed" when it is
            // neither in the new selection nor a locked past date. Everything
            // else (including the preserved past dates) keeps its shift.
            const filteredOutDate = selectedDates.filter((date) => !mergedValidDate.includes(date));
            const formattedFilteredOut = filteredOutDate.map((date) => usToIso(date));

            // Reconcile removals by the day each block BELONGS to (its blockDate,
            // an MM-DD-YYYY start day), not the physical date it lands on. A 24/7
            // shift starting after 19:00 puts its continuation blocks on the next
            // calendar day while they still belong to the selected start day —
            // keying on the physical date would wrongly drop them when the next
            // day is deselected, collapsing the shift to a lone off-grid block.
            setSelectedShift((prevSelectedShifts) => prevSelectedShifts.filter(
                (shift) => !formattedFilteredOut.includes(shiftOwningDayIso(shift)),
            ));

            const sortedDates = mergedValidDate.sort((a, b) => moment(a, 'MM-DD-YYYY').valueOf() - moment(b, 'MM-DD-YYYY').valueOf());
            const selectedVisitDates = sortedDates.map((dates) => {
                const date = formatDate(dates);
                const formattedDate = usToIso(date);
                const dateId = selectedShift?.find((visitDate) => visitDate.date === dates)?.id || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.id || null;
                const saved = selectedShift?.some((visitDate) => visitDate.date === dates) || selectedShift?.some((visitDate) => visitDate.date === formattedDate) || false;
                const shift = selectedShift?.find((visitDate) => visitDate.date === dates)?.shift || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.shift;
                const startTime = selectedShift?.find((visitDate) => visitDate.date === dates)?.startTime || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.startTime;
                const endTime = selectedShift?.find((visitDate) => visitDate.date === dates)?.endTime || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.endTime;
                const shiftType = selectedShift?.find((visitDate) => visitDate.date === dates)?.shiftType || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.shiftType;
                const blockDate = selectedShift?.find((visitDate) => visitDate.date === dates)?.blockDate || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.blockDate;
                return {
                    id: dateId,
                    date,
                    saved,
                    shiftName: shift,
                    shiftType,
                    startTime,
                    endTime,
                    blockDate,
                };
            });

            setVisitDates(prevDate => {
                const filteredSelectedVisitDates = selectedVisitDates.filter(selectedItem => !prevDate.some(prevItem => prevItem.date === selectedItem.date));
                // Keep a block if the day it BELONGS to (blockDate, an MM-DD-YYYY
                // start day, falling back to its own date) is still selected — so
                // overnight continuation blocks that sit on a now-removed next
                // calendar day survive as long as their start day is selected.
                const filteredPrevDate = prevDate.filter(prevItem => sortedDates.includes(visitDateOwningDayUs(prevItem)));
                const mergedVisitDates = [...filteredPrevDate, ...filteredSelectedVisitDates];

                const sortedMergeDates = mergedVisitDates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

                const uniqueVisitDates = sortedMergeDates.reduce((unique: VisitDatesProps[], item) => {
                    const isNullId = item.id === null;
                    const isDuplicate = !isNullId && unique.some(existingItem => existingItem.id === item.id);
                    if (isNullId || !isDuplicate) {
                        unique.push(item);
                    }
                    return unique;
                }, []);
                return uniqueVisitDates;
            });

        }
    };

    function checkForOverlap(newShift: customShiftProps, existingShifts: VisitDatesProps[] | customShiftProps[]): boolean {
        const format = 'MM-DD-YYYY HH:mm:ss';
        const newStart = moment(`${newShift.date} ${newShift.startTime}`, format);
        const newEnd = moment(`${newShift.date} ${newShift.endTime}`, format);

        if (newEnd.isBefore(newStart)) {
            newEnd.add(1, 'day');
        }

        return existingShifts.some(shift => {
            if (shift.startTime === undefined || shift.endTime === undefined) {
                return false;
            }
            const existingStart = moment(`${shift.date} ${shift.startTime}`, format);
            const existingEnd = moment(`${shift.date} ${shift.endTime}`, format);

            if (existingEnd.isBefore(existingStart)) {
                existingEnd.add(1, 'day');
            }

            return newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart);
        });
    }

    const handleSaveShift = (values: SaveShiftValues) => {
        const shifts: customShiftProps[] = [];
        values.dates.sort((a, b) => moment(a, 'MM-DD-YYYY').valueOf() - moment(b, 'MM-DD-YYYY').valueOf());

        // Emergency Request (admin-only Custom + time critical) fully builds its
        // own 7-to-7 grid below, so none of the other per-date shift-building
        // branches must run for it — otherwise they push extra blocks.
        const isEmergencyRequest = values.shift === 'Custom' && values.isTimeCritical;

        // Admin-only "Emergency Request" custom shift: the admin picks the dates
        // on the calendar and sets a Shift Start / Shift End (end enabled, no
        // 12-hour cap). The window runs from the earliest selected day at the
        // start time to the end time, on the latest selected day or the morning
        // after it when the cover runs past midnight, and the system auto-splits
        // it onto the 7-to-7 grid (07:00/19:00) with the odd leading/trailing
        // hours as their own blocks. Blocks on non-selected days are dropped so
        // the calendar's weekend / bank-holiday choices are respected. The
        // shifts are flagged time critical for the review step.
        if (isEmergencyRequest) {
            const startTime = values.customShiftStartTime;
            const endTime = values.customShiftEndTime;
            if (values.dates.length === 0 || !startTime || !endTime) {
                notification.error({
                    message: 'Missing emergency details',
                    description: 'Please select the dates on the calendar and set a start and end time.',
                });
                return;
            }
            const sortedDates = [...values.dates].sort(
                (a, b) => moment(a, 'MM-DD-YYYY').valueOf() - moment(b, 'MM-DD-YYYY').valueOf(),
            );
            const lastDateIso = usToIso(sortedDates[sortedDates.length - 1] ?? '');
            const emergencyBlocks = splitEmergencyWindow(
                usToIso(sortedDates[0] ?? ''),
                startTime,
                // Cover running past midnight ends the morning after the last
                // day picked, so an overnight shift needs one date, not two.
                emergencyWindowEnd(lastDateIso, startTime, endTime),
                endTime,
            );
            if (emergencyBlocks.length === 0) {
                notification.error({
                    message: 'Invalid emergency window',
                    description: 'Please set a valid start and end time (the end must be after the start).',
                });
                return;
            }
            // Keep only blocks whose start day is one the admin selected — this
            // honours the weekend / bank-holiday toggles (already applied to the
            // calendar selection) and any gaps.
            const selectedDaySet = new Set(values.dates);
            emergencyBlocks
                .filter((block) => selectedDaySet.has(block.date))
                .forEach((block) => shifts.push(block));
        }

        // Custom shift logic
        if (values.shift === '24/7' && values.startTime === values.endTime && values.timeSlot !== 'Custom') {
            values.dates.forEach((date) => {
                // ISO form: new Date("MM-DD-YYYY HH:mm") is Invalid Date in
                // Safari, which moment then renders as the literal "Invalid
                // date" into the saved shift.
                const visitStart = `${usToIso(date)}T${values.startTime}`;
                const visitEnd = `${usToIso(date)}T${values.endTime}`;
                const initialEndTime = new Date(visitStart);
                initialEndTime.setHours(initialEndTime.getHours() + 12);
                shifts.push({
                    startTime: moment(new Date(visitStart)).format('HH:mm:ss'),
                    endTime: moment(new Date(initialEndTime)).format('HH:mm:ss'),
                    date: moment(new Date(visitStart)).format('MM-DD-YYYY'),
                    blockDate: moment(new Date(visitStart)).format('MM-DD-YYYY'),
                });
                shifts.push({
                    startTime: moment(new Date(initialEndTime)).format('HH:mm:ss'),
                    endTime: moment(new Date(visitEnd)).format('HH:mm:ss'),
                    date: moment(initialEndTime).format('MM-DD-YYYY'),
                    blockDate: moment(new Date(visitStart)).format('MM-DD-YYYY'),
                });
            });
        }
        // 24/7 with a start time the user picked: each selected day is covered
        // by two 12-hour shifts running from that time. The day is built on its
        // own, so days that are not next to each other behave the same. The
        // 07:00/19:00 grid this replaced turned any other start into a stub
        // block plus two grid blocks, spanning more than a day. That grid now
        // belongs to the admin Emergency path alone (splitEmergencyWindow).
        if (!isEmergencyRequest && values.timeSlot === 'Custom') {
            values.dates.forEach((date) => {
                const startCustom = moment(`${usToIso(date)}T${values.customTimeSlotStartTime}`);

                if (moment(minimumCalendarDate).isSame(moment(), 'day') && startCustom.isBefore(moment())) {
                    notification.error({
                        message: 'Invalid Start Time',
                        description: 'The start shift time you selected is earlier than the current time for the current date. Please select a valid future time to create a shift.',
                    });
                    return;
                }

                buildRotating247Blocks(date, values.customTimeSlotStartTime ?? '')
                    .forEach((block) => shifts.push(block));
            });
        }

        if (values.shift === 'Custom' && !values.isTimeCritical) {
            values.dates.forEach((date) => {
                const visitStart = `${usToIso(date)}T${values.customShiftStartTime}`;
                const visitEnd = `${usToIso(date)}T${values.customShiftEndTime}`;
                const initialEndTime = new Date(visitStart);
                const startCustom = new Date(visitStart);
                const endCustom = new Date(visitEnd);
                let isExactStart = false;
                if (moment(minimumCalendarDate).isSame(moment(), 'day') && moment(startCustom).isBefore(moment())) {
                    notification.error({
                        message: 'Invalid Start Time',
                        description: 'The start shift time you selected is earlier than the current time for the current date. Please select a valid future time to create a shift.',
                    });
                    return;
                }

                const startHour = startCustom.getHours();
                const endHour = endCustom.getHours();
                let shiftDuration;
                if (endHour < startHour) {
                    shiftDuration = (24 - startHour) + endHour;
                } else {
                    shiftDuration = endHour - startHour;
                }
                if (shiftDuration > 12 || shiftDuration === 0) {
                    notification.error({
                        message: 'Invalid Shift Time',
                        description: 'The shift you selected is more than 12 hours. Please select a valid shift time to create a shift.',
                    });
                    return;
                }

                if ((startCustom.getHours() === 7 || startCustom.getHours() === 19) && startCustom.getMinutes() === 0 && (endCustom.getHours() === 7 || endCustom.getHours() === 19) && endCustom.getMinutes() === 0) {
                    isExactStart = true;
                    const firstShiftEndTime = new Date(startCustom);
                    firstShiftEndTime.setHours(startCustom.getHours() + 12);
                    shifts.push({
                        startTime: moment(new Date(startCustom)).format('HH:mm:ss'),
                        endTime: moment(firstShiftEndTime).format('HH:mm:ss'),
                        date: moment(startCustom).format('MM-DD-YYYY'),
                        blockDate: moment(startCustom).format('MM-DD-YYYY'),
                    });
                }

                if (isExactStart) {
                    return;
                }
                if (stepData?.enabled24_7 && endCustom.getHours() !== startCustom.getHours() + 12) {
                    if (startCustom.getHours() < 7 && endCustom.getHours() > 7) {
                        initialEndTime.setHours(7, 0, 0, 0); // Set to 7am same day
                    } else if (startCustom.getHours() < 19 && endCustom.getHours() > 19) {
                        initialEndTime.setHours(19, 0, 0, 0); // Set to 7pm same day
                    } else if (startCustom.getHours() > 19 && endCustom.getHours() > 7 && endCustom.getHours() < 19) {
                        initialEndTime.setDate(initialEndTime.getDate() + 1);
                        initialEndTime.setHours(7, 0, 0, 0); // Next day 7am
                    } else {
                        initialEndTime.setHours(endCustom.getHours(), endCustom.getMinutes(), 0, 0);
                    }
                } else {
                    initialEndTime.setDate(initialEndTime.getDate() + 1);
                    initialEndTime.setHours(endCustom.getHours(), endCustom.getMinutes(), 0, 0);
                }
                const newShift = {
                    startTime: moment(startCustom).format('HH:mm:ss'),
                    endTime: moment(initialEndTime).format('HH:mm:ss'),
                    date: moment(startCustom).format('MM-DD-YYYY'),
                    blockDate: moment(startCustom).format('MM-DD-YYYY'),
                };
                const hasOverlap = checkForOverlap(newShift, shifts);

                // Check for overlap for the current date's newShift before adding to shifts array
                if (hasOverlap) {
                    notification.error({
                        message: 'Shift Overlap',
                        description: 'Your requested shift start/end time overlaps with an existing shift. Please enter a different start time.',
                    });
                    return;
                }
                shifts.push(newShift);
                const nextStartTime = initialEndTime;

                const nextEndTime = stepData?.enabled24_7 ? new Date(visitEnd) : new Date(nextStartTime);
                if (!stepData?.enabled24_7) {
                    nextEndTime.setHours(startCustom.getHours() + 12);
                    nextEndTime.setMinutes(endCustom.getMinutes());
                }
                if (nextEndTime.getHours() === nextStartTime.getHours() && nextEndTime.getMinutes() === nextStartTime.getMinutes()) {
                    return;
                }
                shifts.push({
                    startTime: moment(nextStartTime).format('HH:mm:ss'),
                    endTime: moment(nextEndTime).format('HH:mm:ss'),
                    date: moment(nextStartTime).format('MM-DD-YYYY'),
                    blockDate: moment(startCustom).format('MM-DD-YYYY'),
                });
            });
        }
        if (values.timeSlot !== 'Custom' && values.dates.length > 0 && values.shift !== 'Custom') {
            // Exclude the dates we're rewriting from the overlap source —
            // otherwise single-shift mode (where the same dates are being
            // resaved with a new shift) trips a self-overlap false-alarm.
            const existingShiftsForOverlap = visitDates.filter(
                (vd) => !values.dates.includes(vd.date),
            );
            let hasOverlap = false;
            values.dates.forEach((date) => {
                const newShift = {
                    startTime: values.startTime || '',
                    endTime: values.endTime || '',
                    date,
                    blockDate: null,
                };

                if (checkForOverlap(newShift, existingShiftsForOverlap)) {
                    notification.error({ message: 'Shift Overlap', description: 'Your requested shift start/end time overlaps with an existing shift. Please enter a different start time.' });
                    hasOverlap = true;
                }
            });
            if (hasOverlap) {
                return;
            }
        }

        let hasInvalidTime = false;
        if (!isEmergencyRequest && values.selectedDates?.includes(moment(minimumCalendarDate).format('MM-DD-YYYY'))) {
            values.selectedDates?.forEach((date) => {
                const todayDate = new Date();
                const slotDate = usToIso(date);
                const time = values?.startTime || (values.shiftType === '30' ? values.customShiftStartTime : values.customTimeSlotStartTime);
                const endTime = values.shiftType === '30' ? values.customShiftEndTime : values?.endTime;
                const dateTime = new Date(`${slotDate}T${time}`);
                const endDateTime = new Date(`${slotDate}T${endTime}`);

                // If end time is less than start time, it means the shift spans to the next day
                if (endTime && time && endTime < time) {
                    endDateTime.setDate(endDateTime.getDate() + 1);
                }

                const isTimeInThePast = dateTime < todayDate;
                const isEndTimeInThePast = endDateTime < todayDate;
                // check if today is open day
                const today = moment().format('MM-DD-YYYY');
                const todayDayOfWeek = moment(today).format('dddd');
                const isOpenDay = siteAvailability.site?.openDays?.some(openDay => openDay.day === todayDayOfWeek);

                // An active approved visit for today matching this shift
                // type also unlocks past-start booking — same rule as the
                // shift-options filter so non-open-day approvals can be
                // joined by other requesters.
                const todayYmd = moment().format('YYYY-MM-DD');
                const hasActiveApprovalMatch = (stepData.approvedVisits ?? []).some((av) => {
                    if (av.date !== todayYmd) return false;
                    if (av.shift !== values.shift) return false;
                    const avStart = moment(av.startTime, 'HH:mm:ss');
                    const avEnd = moment(av.endTime, 'HH:mm:ss');
                    if (avEnd.isBefore(avStart)) avEnd.add(1, 'day');
                    return avEnd.isAfter(moment());
                });
                const allowPastStart = isOpenDay || hasActiveApprovalMatch;

                // Check if this is today's date and handle time validations
                const isToday = moment(minimumCalendarDate).isSame(moment(), 'day')
                    && values.selectedDates?.includes(moment(minimumCalendarDate).format('MM-DD-YYYY'));

                if (isToday && isTimeInThePast && !allowPastStart) {
                    notification.error({
                        message: 'Invalid Start Time',
                        description: 'The start shift time you selected is earlier than the current time for the current date. Please select a valid future time to create a shift.',
                    });
                    hasInvalidTime = true;
                }

                if (isToday && isEndTimeInThePast) {
                    notification.error({
                        message: 'Invalid End Time',
                        description: 'The end shift time you selected is earlier than the current time for the current date. Please select a valid future time to create a shift.',
                    });
                    hasInvalidTime = true;
                }

                if (isOpenDay && !isEndTimeInThePast) {
                    shifts.push({
                        startTime: moment(dateTime).format('HH:mm:ss'),
                        endTime: moment(endDateTime).format('HH:mm:ss'),
                        date: moment(dateTime).format('MM-DD-YYYY'),
                        blockDate: moment(dateTime).format('MM-DD-YYYY'),
                    });
                }
            });
        }

        // Stop here if either of the today-block validations toasted an
        // error. Previously the function fell through and the shift still
        // got assigned via the map below, with the toast appearing but
        // having no effect on state — the user could even click Next.
        if (hasInvalidTime) {
            return;
        }

        // Same self-overlap guard as above: a date being rewritten can't
        // overlap with its own previous entry.
        const rewrittenDates = new Set([
            ...values.dates,
            ...shifts.map((s) => s.date),
        ]);
        const existingShiftsForOverlap = visitDates.filter((vd) => !rewrittenDates.has(vd.date));
        const hasOverlap = shifts.some((shift) => checkForOverlap(shift, existingShiftsForOverlap));
        if (hasOverlap) {
            notification.error({ message: 'Shift Overlap', description: 'Your requested shift start/end time overlaps with an existing shift. Please enter a different start time.' });
            return;
        }

        // Single-shift mode: drop existing entries for any date that
        // `shifts` is about to push a replacement for. Without this, the
        // push loop below treats the new entry as "different startTime →
        // append", and the same date ends up listed twice. Future-date
        // regular shifts (no `shifts` push, no entry in this set) keep
        // their existing rows so the map below can update them in place.
        // Multi-shift mode keeps the full visitDates so per-date
        // assignments aren't disturbed.
        const shiftDatesBeingRewritten = new Set(shifts.map((s) => s.date));
        let completeVisitDates = values.isSameShift
            ? visitDates.filter((vd) => !shiftDatesBeingRewritten.has(vd.date))
            : [...visitDates];

        // Emergency Request: the emergency window fully determines which days
        // get a shift. A day the admin selected only to mark the window's end
        // (e.g. end 07:00 on the 20th → the window's last block is the 19th's
        // overnight shift ending 07:00 on the 20th) produces no block of its
        // own, so its unsaved calendar placeholder must be dropped — otherwise
        // it lingers as an empty shift row for that trailing day.
        if (isEmergencyRequest) {
            const emergencyBlockDates = new Set(shifts.map((s) => s.blockDate));
            completeVisitDates = completeVisitDates.filter(
                (vd) => vd.saved || emergencyBlockDates.has(vd.date),
            );
        }

        // A single-entry shift (Day Shift, Night Shift, Open Day) produces
        // exactly one row per date, whereas 24/7 (and split Custom shifts) split
        // a date into two. When a date is switched FROM a split shift TO a
        // single-entry one, the in-place update below would rewrite BOTH old
        // rows with the new times, leaving the date duplicated (e.g. 24/7 → Day
        // Shift showed 6 rows instead of 3). Collapse each rewritten date back
        // to one row first, keeping the first so its id/saved state survives.
        const newShiftSplitsDates = values.shift === '24/7'
            || values.shift === 'Custom'
            || values.timeSlot === 'Custom';
        if (!newShiftSplitsDates) {
            const seenRewrittenDate = new Set<string>();
            completeVisitDates = completeVisitDates.filter((vd) => {
                if (!values.dates.includes(vd.date)) {
                    return true;
                }
                if (seenRewrittenDate.has(vd.date)) {
                    return false;
                }
                seenRewrittenDate.add(vd.date);
                return true;
            });
        }

        const uniqueblockDateShifts = shifts.filter((shift, index, self) => index === self.findIndex((t) => t.blockDate === shift.blockDate));

        uniqueblockDateShifts.forEach(shiftToUpdate => {
            const matchingIndex = completeVisitDates.findIndex(visitDate => visitDate.date === shiftToUpdate.blockDate && !visitDate.blockDate);

            if (matchingIndex > -1) {
                completeVisitDates[matchingIndex] = {
                    ...completeVisitDates[matchingIndex], // Preserve existing properties
                    date: shiftToUpdate.date,
                    startTime: shiftToUpdate.startTime,
                    endTime: shiftToUpdate.endTime,
                    blockDate: shiftToUpdate.blockDate,
                    saved: completeVisitDates[matchingIndex]?.saved || false,
                    id: completeVisitDates[matchingIndex]?.id || null,
                };
            }
        });

        shifts.forEach((shift) => {
            if (!completeVisitDates.some(date => date.date === shift.date && date.startTime === shift.startTime)) {
                completeVisitDates.push({
                    startTime: shift.startTime,
                    endTime: shift.endTime,
                    startDate: shift.date,
                    endDate: shift.date,
                    date: shift.date,
                    saved: false,
                    id: null,
                    blockDate: shift.blockDate,
                });
            }
        });

        const updatedVisitDates = completeVisitDates.map((date) => {
            const customShift = shifts.find(shift => shift.date === date.date && shift.startTime === date.startTime);
            if (customShift) {
                return {
                    ...date,
                    saved: true,
                    shiftName: values.shift,
                    startTime: customShift.startTime,
                    endTime: customShift.endTime,
                    blockDate: customShift.blockDate,
                    shiftType: values.shiftType ? parseInt(values.shiftType, 10) : 0,
                    bookingTimeCritical: values.isTimeCritical ? true : Boolean(date.bookingTimeCritical),
                };
            } if (values.dates.includes(date.date) && (date.startTime || values.startTime)) {
                return {
                    ...date,
                    saved: true,
                    shiftName: values.shift,
                    startTime: values.startTime || date.startTime,
                    endTime: values.endTime || date.endTime,
                    shiftType: values.shiftType ? parseInt(values.shiftType, 10) : 0,
                };
            }
            return date;
        });

        // sort by date
        updatedVisitDates.sort((a, b) => {
            const dateA = a.date || '';
            const dateB = b.date || '';

            // Compare dates
            if (dateA < dateB) {
                return -1;
            } if (dateA > dateB) {
                return 1;
            }
            return 0;
        });

        // Rebuild selectedShift as a fresh mirror of updatedVisitDates'
        // saved entries. The previous implementation appended a new entry
        // per save without dropping prior entries, so resaving the same
        // date stacked old-and-new (e.g. Day Shift + Night Shift on the
        // same day). On Next, that stale list was the payload — backend
        // kept the old shift and the user saw it on return.
        const newSelectedShifts: ShiftSelected[] = [];
        updatedVisitDates.forEach((day, index) => {
            if (!(day.saved && day.shiftName && day.startTime && day.endTime)) {
                return;
            }
            const dateString = day.date || '';
            const formattedDate = usToIso(dateString);
            newSelectedShifts.push({
                ...day,
                id: day.id || null,
                _tempIndex: index,
                date: formattedDate,
                day: dateString,
                shift: day.shiftName,
                shiftType: day.shiftType,
                startTime: day.startTime,
                endTime: day.endTime,
                blockDate: day.blockDate,
            });
        });
        setSelectedShift(newSelectedShifts);

        setVisitDates(updatedVisitDates);
    };

    function transformShiftData(shifts: Shift[]) {
        const shiftOption: ShiftOption[] = [];

        // A guest booking link can restrict which shift types are bookable.
        // When present, only offer the permitted SiteShift type ids (0=Day,
        // 10=Night, 20=24/7, 30=Custom, 40=Open Day). Undefined = no limit.
        const { allowedShiftTypes } = stepData;
        const isTypeAllowed = (typeId: number | string | undefined) => (
            !allowedShiftTypes || allowedShiftTypes.includes(Number(typeId))
        );

        // Iterate over each shift type
        shifts.forEach((shift, index) => {
            if (!isTypeAllowed(shift.typeId)) {
                return;
            }
            // Find if the shift type already exists in shiftOptions
            const existingShift = shiftOption.find(option => option.name === shift.type);

            // If the shift type doesn't exist, create a new entry
            if (!existingShift) {
                const newShiftOption = {
                    id: index,
                    name: shift.type,
                    value: shift.typeId,
                    timeSlots: [{
                        label: `${formatTime(shift.startTime)} - ${formatTime(shift.endTime)}`,
                        startTime: shift.startTime,
                        endTime: shift.endTime,
                    }],
                };
                shiftOption.push(newShiftOption);
            } else {
                existingShift.timeSlots.push({
                    label: `${formatTime(shift.startTime)} - ${formatTime(shift.endTime)}`,
                    startTime: shift.startTime,
                    endTime: shift.endTime,
                });
            }
        });

        if (isTypeAllowed('30')) {
            const newShiftOptionCustom = {
                id: 0,
                name: 'Custom',
                value: '30',
                timeSlots: [],
            };
            shiftOption.push(newShiftOptionCustom);
        }

        // check if select day is an open day then add "Open Day" shift
        const matchingEverySelectDayWithOpenDay = isTypeAllowed('40') ? selectedDates.map(date => {
            const day = moment(date, 'MM-DD-YYYY').format('dddd').toLocaleLowerCase();
            const matchOpenDay = siteAvailability.site?.openDays?.find(open => open.day.toLocaleLowerCase() === day);
            return matchOpenDay;
        }) : [];

        if (matchingEverySelectDayWithOpenDay.every(day => day !== undefined) && !isEmpty(matchingEverySelectDayWithOpenDay) && matchingEverySelectDayWithOpenDay[0]) {
            const startTimes = matchingEverySelectDayWithOpenDay?.map(days => days?.startTime);
            const endTimes = matchingEverySelectDayWithOpenDay?.map(days => days?.endTime);
            if (startTimes && endTimes) {
                if (startTimes.every(time => time === startTimes[0]) && endTimes.every(time => time === endTimes[0])) {
                    const { startTime } = matchingEverySelectDayWithOpenDay[0];
                    const { endTime } = matchingEverySelectDayWithOpenDay[0];
                    const latestId = latestShiftOptionId(shiftOption.map(option => option?.id));

                    if (startTime === endTime) {
                        // 24/7 sites: SiteResource synthesises an
                        // open-day window of start==end==07:00, which
                        // is the marker for "all day". Surface that
                        // as two pickable halves so the user can book
                        // a day or night Open Day independently. Use
                        // pure string arithmetic for the split point —
                        // see addHoursToTime's docblock for why
                        // Date.setHours/getHours can't be trusted in
                        // half-hour timezones (this is the bug that
                        // produced "Open Day 07:00 AM - 11:30 PM" for
                        // IST users on request 936).
                        const halfwayTime = addHoursToTime(startTime, 12);
                        const firstShiftOption = {
                            id: latestId + 1,
                            name: `Open Day ${formatTime(startTime)} - ${formatTime(halfwayTime)}`,
                            value: '40',
                            timeSlots: [{
                                label: `${formatTime(startTime)} - ${formatTime(halfwayTime)}`,
                                startTime,
                                endTime: halfwayTime,
                            }],
                        };
                        const secondShiftOption = {
                            id: latestId + 2,
                            name: `Open Day ${formatTime(halfwayTime)} - ${formatTime(endTime)}`,
                            value: '40',
                            timeSlots: [{
                                label: `${formatTime(halfwayTime)} - ${formatTime(endTime)}`,
                                startTime: halfwayTime,
                                endTime: startTime,
                            }],
                        };
                        shiftOption.push(firstShiftOption, secondShiftOption);
                    } else {
                        const newOpenShiftOption = {
                            id: latestId + 1,
                            name: `Open Day ${formatTime(startTime)} - ${formatTime(endTime)}`,
                            value: '40',
                            timeSlots: [{
                                label: `${formatTime(startTime)} - ${formatTime(endTime)}`,
                                startTime,
                                endTime,
                            }],
                        };
                        shiftOption.push(newOpenShiftOption);
                    }

                }
            }
        }

        // Move Open Day options to the top when the selected dates are open days
        const openDayOptions = shiftOption.filter((opt) => opt.value === '40');
        const otherOptions = shiftOption.filter((opt) => opt.value !== '40');
        return openDayOptions.length > 0 ? [...openDayOptions, ...otherOptions] : shiftOption;
    }

    const handleRemoveShift = (date: string) => {
        const dateToRemove = visitDates.find(visitDate => visitDate.date === date && visitDate.blockDate);
        const blockDateToRemove = dateToRemove?.blockDate;
        const updatedVisitDates = visitDates.map((visitDate) => {
            if (visitDate.date === date && !visitDate.blockDate) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                };
            } if (visitDate.blockDate === blockDateToRemove && blockDateToRemove !== undefined) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                    blockDate: visitDate.blockDate,
                };
            }
            return visitDate;
        });

        const blockDateExists = updatedVisitDates.some((visitDate) => visitDate.blockDate === blockDateToRemove);
        if (blockDateExists && blockDateToRemove !== undefined) {
            const uniqueblockDates = new Set(); // Set to track unique blockDates
            const filteredVisitDates = updatedVisitDates.filter(day => {
                if (!day.blockDate || !uniqueblockDates.has(day.blockDate) || day.blockDate !== blockDateToRemove) {
                    uniqueblockDates.add(day.blockDate);
                    return true;
                }
                return false;
            });
            // update blockDate to be undefinded
            const updatedblockDateDates = filteredVisitDates.map((visitDate) => {
                if (visitDate.blockDate === blockDateToRemove) {
                    return {
                        ...visitDate,
                        date: visitDate.blockDate || '',
                        blockDate: null,
                    };
                }
                return visitDate;
            });
            updatedblockDateDates.sort((a, b) => {
                const dateA = a.date || '';
                const dateB = b.date || '';

                // Compare dates
                if (dateA < dateB) {
                    return -1;
                } if (dateA > dateB) {
                    return 1;
                }
                return 0;
            });
            setVisitDates(updatedblockDateDates);
            setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(shift => shift.blockDate !== blockDateToRemove));
        } else {
            // Deduplicate unsaved entries on the same date (e.g. after
            // removing a 24/7 shift with two blocks on the same day).
            const seenDates = new Set<string>();
            const deduplicated = updatedVisitDates.filter((vd) => {
                if (vd.saved) return true;
                if (seenDates.has(vd.date)) return false;
                seenDates.add(vd.date);
                return true;
            });
            setVisitDates(deduplicated);
        }
        setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(
            shift => !shiftIsOnCalendarDate(shift, date),
        ));
    };

    const handleEditShift = (date: string) => {
        const dateToRemove = visitDates.find(visitDate => visitDate.date === date && visitDate.blockDate);
        const blockDateToRemove = dateToRemove?.blockDate;
        const updatedVisitDates = visitDates.map((visitDate) => {
            if (visitDate.date === date && !visitDate.blockDate) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    shiftType: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                };
            } if (visitDate.blockDate === blockDateToRemove && blockDateToRemove !== undefined) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    shiftType: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                    blockDate: visitDate.blockDate,
                    date: visitDate.blockDate || '',
                };
            }
            return visitDate;
        });

        const blockDateExists = updatedVisitDates.some((visitDate) => visitDate.blockDate === blockDateToRemove);
        if (blockDateExists && blockDateToRemove !== undefined) {
            const uniqueblockDates = new Set(); // Set to track unique blockDates
            const filteredVisitDates = updatedVisitDates.filter(day => {
                if (!day.blockDate || !uniqueblockDates.has(day.blockDate) || day.blockDate !== blockDateToRemove) {
                    uniqueblockDates.add(day.blockDate);
                    return true;
                }
                return false;
            });
            // update blockDate to be undefinded
            const updatedblockDateDates = filteredVisitDates.map((visitDate) => {
                if (visitDate.blockDate === blockDateToRemove) {
                    return {
                        ...visitDate,
                        blockDate: null,
                    };
                }
                return visitDate;
            });
            updatedblockDateDates.sort((a, b) => {
                const dateA = a.date || '';
                const dateB = b.date || '';

                // Compare dates
                if (dateA < dateB) {
                    return -1;
                } if (dateA > dateB) {
                    return 1;
                }
                return 0;
            });
            setVisitDates(updatedblockDateDates);
            setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(shift => shift.blockDate !== blockDateToRemove));
        } else {
            setVisitDates(updatedVisitDates);
        }
        setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(
            shift => !shiftIsOnCalendarDate(shift, date),
        ));
    };

    useEffect(() => {
        if (stepData?.shifts) {
            const shifts = transformShiftData(stepData?.shifts);
            setShiftOptions(shifts);
        }
    }, [stepData?.shifts]);

    useEffect(() => {
        setSelectedShift(visitRequestState.dates || []);
        const dates = visitRequestState.dates?.reduce((acc: string[], curr) => {
            if (curr.blockDate === null) {
                if (!acc.includes(curr.date)) {
                    acc.push(curr.date);
                }
            } else {
                const alreadyIncluded = acc.some(date => {
                    const dateEntry = visitRequestState?.dates?.find(d => d.date === date);
                    return dateEntry?.blockDate === curr.blockDate && dateEntry?.date === curr.date;
                });
                if (!alreadyIncluded) {
                    acc.push(curr.date);
                }
            }
            return acc;
        }, []) || [];

        // set initial dates to setVisitDates
        // Dedupe by id first (avoid double-counting the same backend row),
        // then by date+blockDate so a stale row left behind for the same
        // calendar day from a previous bug doesn't render alongside the
        // current row. Keep the entry with the highest id (latest insert)
        // so the most-recent shift name wins.
        const dedupedById = visitRequestState.dates?.reduce((acc: {
            shift: string;
            id?: number;
            date:string;
            startTime: string;
            endTime: string;
            shiftType: string;
            blockDate?: string;}[], curr) => {
            if (curr.id === null || !acc.some(item => item.id === curr.id)) {
                acc.push(curr);
            }
            return acc;
        }, []) || [];

        const latestByDateKey = new Map<string, typeof dedupedById[number]>();
        dedupedById.forEach((entry) => {
            const key = `${entry.date}|${entry.blockDate ?? ''}`;
            const current = latestByDateKey.get(key);
            if (!current || ((entry.id ?? 0) > (current.id ?? 0))) {
                latestByDateKey.set(key, entry);
            }
        });
        const allDate = Array.from(latestByDateKey.values());
        const currentVisitDates = allDate.map((day) => {
            const date = formatDate(day.date);
            const formattedDate = usToIso(date);
            const saved = visitRequestState.dates?.some((visitDate) => visitDate.date === day.date) || visitRequestState.dates?.some((visitDate) => visitDate.date === formattedDate) || false;

            return {
                id: day.id || null,
                date,
                saved,
                shiftName: day.shift,
                shiftType: day.shiftType,
                startTime: day.startTime,
                endTime: day.endTime,
                blockDate: day.blockDate,
            };
        });
        currentVisitDates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setSelectedDates(dates.map(date => formatDate(date)));
        setVisitDates(currentVisitDates);
    }, [visitRequestState.dates]);

    useEffect(() => {
        if (visitRequestState.numberOfOfficers) {
            setNumberOfOfficers(visitRequestState.numberOfOfficers);
        }
    }, [visitRequestState.numberOfOfficers]);

    useEffect(() => {
        if (stepData.costCode) {
            setCode(stepData.costCode.costCentre || '');
            setEmail(stepData.costCode.costApprovalEmail || '');
        }
    }, [stepData.costCode]);

    useEffect(() => {
        const latestShiftStartTime = stepData.shifts?.reduce((latestTime, shift) => {
            const shiftStartTime = moment(shift.startTime, 'HH:mm:ss');
            return shiftStartTime.isAfter(latestTime) ? shiftStartTime : latestTime;
        }, moment('00:00:00', 'HH:mm:ss'));

        // check if today is open day
        const todayDayName = moment().format('dddd');
        const todayOpenDay = stepData.openDays?.find(openDay => openDay.day === todayDayName);
        const isEnabled24_7 = stepData.enabled24_7;
        const todayOpenEndTime = moment(todayOpenDay?.endTime, 'HH:mm:ss');
        const isOpenDayActiveNow = !!todayOpenDay
            && (todayOpenEndTime.isAfter(moment()) || isEnabled24_7);

        // Approved visits whose window still has time remaining today.
        // Same source used by the "View Open Days & Upcoming Bookings"
        // calendar modal — if anyone (any dept/company) has been approved
        // for today and the slot hasn't fully ended, treat today as
        // bookable for the matching shift type.
        const todayYmd = moment().format('YYYY-MM-DD');
        const activeApprovalsToday = (stepData.approvedVisits ?? []).filter(av => {
            if (av.date !== todayYmd) return false;
            const start = moment(av.startTime, 'HH:mm:ss');
            const end = moment(av.endTime, 'HH:mm:ss');
            if (end.isBefore(start)) end.add(1, 'day');
            return end.isAfter(moment());
        });

        if (latestShiftStartTime?.isAfter(moment())) {
            setMinimumCalendarDate(moment().format('YYYY-MM-DD'));
        } else if (isOpenDayActiveNow || activeApprovalsToday.length > 0) {
            // Today is selectable when the open-day window still has time
            // remaining OR a matching approved request is still active.
            setMinimumCalendarDate(moment().format('YYYY-MM-DD'));
        } else {
            setMinimumCalendarDate(moment().add(1, 'day').format('YYYY-MM-DD'));
        }

        const shiftOptionsToday = stepData.shifts?.filter(shift => {
            const shiftStartTime = moment(shift.startTime, 'HH:mm:ss');
            const shiftEndTime = moment(shift.endTime, 'HH:mm:ss');
            // Handle overnight shifts (e.g. Night Shift 19:00 → 07:00) so
            // the end time is anchored to tomorrow before we compare.
            if (shiftEndTime.isBefore(shiftStartTime)) {
                shiftEndTime.add(1, 'day');
            }

            // Hard exclude: if the shift's end has already passed today
            // there's no remaining slot to book, so it shouldn't appear
            // even if an approval or open-day window would otherwise
            // unlock it.
            if (!shiftEndTime.isAfter(moment())) {
                return false;
            }

            // Original rule: shift hasn't started yet.
            if (shiftStartTime.isAfter(moment())) {
                return true;
            }

            // Open Day shift: keep visible while the open-day window today
            // still has time left (typeId '40' matches SiteShift::TYPE_OPEN_DAY).
            if (shift.typeId === '40' && isOpenDayActiveNow) {
                return true;
            }

            // Approved-visit override: a non-open-day shift becomes
            // selectable when an existing approved booking on today shares
            // the same shift type and still has time left.
            if (activeApprovalsToday.some(av => av.shift === shift.type)) {
                return true;
            }

            return false;
        });

        const allOtherDatesSaved = visitDates.every(visit => {
            const shiftDate = moment(visit.date);

            if (shiftDate.isSame(moment(), 'day')) {
                return visit.saved === false;
            }
            return visit.saved === true;
        });

        const todayDate = moment(minimumCalendarDate).format('MM-DD-YYYY');
        if (shiftOptionsToday && ((allOtherDatesSaved && selectedDates.length > 0) || (todayDate && selectedDates.length === 1)) && selectedDates.includes(todayDate)) {
            setShiftOptions(transformShiftData(shiftOptionsToday));
        } else if (stepData?.shifts) {
            setShiftOptions(transformShiftData(stepData.shifts));
        }

    }, [stepData.openDays, stepData.shifts, selectedDates, selectedShift]);

    return (
        <RequestFormLayout
            currentStep={2}
            isNextEnabled
            isBackEnabled={!navState?.amending && !flow.guestMode}
            onBackClick={handleBackClick}
            onNextClick={handleNextClick}>
            <>
                <DateSelector
                    handleDatesSelect={handleDatesSelect}
                    selectedDates={selectedDates}
                    selectedShift={selectedShift}
                    siteAvailability={siteAvailability}
                    holidays={holidays}
                    minDate={minimumCalendarDate}
                    restrictToOpenDays={restrictToOpenDays} />
                <ShiftSelector
                    visitDates={visitDates}
                    shiftOptions={shiftOptions}
                    handleRemoveShift={handleRemoveShift}
                    handleSaveShift={handleSaveShift}
                    handleEditShift={handleEditShift}
                    totalHours={stepData.totalHours}
                    enabled24_7={stepData.enabled24_7}
                    onChangetotalHours={(value) => setSaveTotalHours(value)}
                    openDays={stepData.openDays}
                    bankHolidays={bankHolidayList}
                    approvedVisits={stepData.approvedVisits ?? []}
                    isAdmin={isAdmin}
                    lockToEmergency={isEmergencyAmendment}
                    numberOfOfficers={numberOfOfficers}
                    onChangeNumberOfOfficers={setNumberOfOfficers} />
                <CostCode
                    code={code}
                    email={email}
                    requireEmail={requireEmail}
                    billedHours={saveTotalHours}
                    onChangeCode={(value) => setCode(value)}
                    onChangeEmail={(value) => setEmail(value)} />
                <BankHolidayWarningModal
                    open={bankHolidayModal.open}
                    dates={bankHolidayCharges}
                    onBack={() => setBankHolidayModal({ open: false, navigationState: null })}
                    onContinue={() => {
                        const { navigationState } = bankHolidayModal;
                        setBankHolidayAcknowledged(true);
                        setBankHolidayModal({ open: false, navigationState: null });
                        // Straight on with the rest of handleNextClick: re-entering
                        // it would read the acknowledgement before React has set
                        // it and reopen the warning.
                        handleNextClick(navigationState ?? {}, true);
                    }} />
            </>
        </RequestFormLayout>
    );
};

export default DateSelectionForm;
